import { Company } from '../interfaces/company.interface';
import { AssistantPrompt } from '../interfaces/assistant-prompt.interface';
import { safeParseJSON } from './safe-parse-json.util';

export function transformCompanyData(company: any, report: any): Company {
  // Transform insights object to assistant_prompts array for backward compatibility
  let assistantPrompts: AssistantPrompt[] = [];

  // Check if we have the new insights structure
  if (company.insights && typeof company.insights === 'object') {
    // Convert insights object to assistant_prompts array
    assistantPrompts = Object.entries(company.insights).map(([key, insight]: [string, any]) => {
      const tag = insight.tag;

      // Special handling for ASK_ASSISTANT_SPECIAL_BOX
      if (tag === 'ASK_ASSISTANT_SPECIAL_BOX') {
        const specialContent = insight.response || {};
        return {
          id: insight.id || key, // Use key as fallback id
          tag: tag,
          content: {
            short: specialContent.short || null,
            current_stage_of_the_fund: specialContent.current_stage_of_the_fund || null,
            investment_stages: specialContent.investment_stages || null,
            summary_investment_thesis: specialContent.summary_investment_thesis || null,
            ticket_size: specialContent.ticket_size || null,
            top_startups: specialContent.top_startups || null
          },
          cost: insight.cost || 0,
        };
      }
      // General handling for other tags
      else {
        // Get content from response
        let content = insight.response?.message?.content ?? insight.response ?? '';

        // Try to parse content if it's a JSON string with 'short' property
        if (typeof content === 'string' &&
          content.trim().startsWith('{') &&
          content.includes('"short"')) {
          try {
            const parsedContent = JSON.parse(content);

            // If parsed content has short/long properties, use it as is
            if (parsedContent && typeof parsedContent === 'object' && 'short' in parsedContent) {
              content = parsedContent;
            }
          } catch (e) {
            // If parsing fails, keep the original string
          }
        }

        return {
          id: insight.id || key, // Use key as fallback id
          tag: tag || '',
          content: content,
          cost: insight.cost || 0,
        };
      }
    });
  }
  // Fallback to old structure if insights doesn't exist
  else if (company.assistant_prompts && Array.isArray(company.assistant_prompts)) {
    assistantPrompts = company.assistant_prompts.map((prompt: any) => {
      // Same transformation logic as before
      if (prompt.tag === 'ASK_ASSISTANT_SPECIAL_BOX') {
        const specialContent = prompt.response || {};
        return {
          id: prompt.id,
          tag: prompt.tag,
          content: {
            short: specialContent.short || null,
            current_stage_of_the_fund: specialContent.current_stage_of_the_fund || null,
            investment_stages: specialContent.investment_stages || null,
            summary_investment_thesis: specialContent.summary_investment_thesis || null,
            ticket_size: specialContent.ticket_size || null,
            top_startups: specialContent.top_startups || null
          },
          cost: prompt.cost || 0,
        };
      } else {
        let content = prompt.response?.message?.content ?? prompt.response ?? '';

        if (typeof content === 'string' &&
          content.trim().startsWith('{') &&
          content.includes('"short"')) {
          try {
            const parsedContent = JSON.parse(content);
            if (parsedContent && typeof parsedContent === 'object' && 'short' in parsedContent) {
              content = parsedContent;
            }
          } catch (e) {
            // Keep original string
          }
        }

        return {
          id: prompt.id,
          tag: prompt.tag || '',
          content: content,
          cost: prompt.cost || 0,
        };
      }
    });
  }

  // Rest of the transformation remains unchanged
  return {
    report_id: report.id ? parseInt(report.id, 10) : null,
    id: company.id || null,
    name: company.name || null,
    person_name: report.name || null,
    logo_url: company.enhanced_data?.logo_url || null,
    city: company.enhanced_data?.city || null,
    state: company.enhanced_data?.state || null,
    country: company.enhanced_data?.country || null,
    short_description: company.enhanced_data?.short_description || null,
    parsed_linkedin_url: company.parsed_linkedin_url || null,
    website: company.website || null,
    blog: company.enhanced_data?.blog_url || null,
    phone: {
      number: company.enhanced_data?.primary_phone?.number || null,
      source: company.enhanced_data?.primary_phone?.source || null,
    },
    social_media: {
      linkedin_url: company.enhanced_data?.linkedin_url || null,
      facebook_url: company.enhanced_data?.facebook_url || null,
      twitter_url: company.enhanced_data?.twitter_url || null,
    },
    insights: company.insights || null, // Store the original insights object
    estimated_num_employees: company.enhanced_data?.estimated_num_employees || null,
    keywords: company.enhanced_data?.keywords || [],
    technologies: company.enhanced_data?.technology_names || [],
    industry: company.enhanced_data?.industry || null,
    icp_refine_score: company.icp_refine_score != null ? company.icp_refine_score : null,
    icp_score: company.icp_score != null ? company.icp_score : null,
    assistant_prompts: assistantPrompts, // Keep this for backward compatibility
    revenue: company.revenue || 'N/A',
    profit: company.profit || 'N/A',
    // Funding for the ACTIVE company. Prefer an explicit value, then the
    // company's Apollo enhanced_data, so it follows a company switch instead of
    // showing the report-level (original company's) figure.
    funding: company.funding || company.enhanced_data?.total_funding_printed || 'N/A',
    // Per-company Veridion data so the report's Locations + company-details
    // modal follow the active company on a switch (instead of report-level data).
    veridion_data: company.veridion_data || null,
    employees: company.employees || 'N/A',
    search_company_icp_detail: company.searchCompanyIcpDetail || []
  };
}
